/** Maximum wave components the shader supports (upper bound for all wave sets). */
export const MAX_WAVES = 64;

// Topographic contour shader. Mirrors the CPU math in gerstner.ts:
// height y = Σ Aᵢ sin(kᵢ (Dᵢ·P) − ωᵢ t). There is NO color fill — only contour
// isolines are drawn over an optional background.
export const TOPO_FRAGMENT_SHADER = /* glsl */ `#version 300 es
precision highp float;

#define MAX_WAVES ${MAX_WAVES}
const float PI = 3.141592653589793;

uniform vec2  u_resolution;
uniform float u_time;
uniform float u_extent;

uniform int   u_waveCount;
uniform float u_dir[MAX_WAVES];
uniform float u_wavelength[MAX_WAVES];
uniform float u_amplitude[MAX_WAVES];
uniform float u_speed[MAX_WAVES];
uniform float u_steepness;       // 0 = smooth sine, higher = sharp Stokes crests

uniform bool  u_contourEnabled;
uniform float u_contourInterval;
uniform float u_contourWidth;
uniform vec4  u_contourColor;
uniform int   u_emphasisEvery;   // 0 = no index (bold) lines
uniform float u_emphasisWidth;
uniform vec4  u_emphasisColor;

uniform vec4  u_background;

out vec4 fragColor;

float heightAt(vec2 p) {
  float y = 0.0;
  for (int i = 0; i < MAX_WAVES; i++) {
    if (i >= u_waveCount) break;
    float k = (2.0 * PI) / u_wavelength[i];
    float omega = u_speed[i] * k;
    vec2 d = vec2(cos(u_dir[i]), sin(u_dir[i]));
    float phase = k * dot(d, p) - omega * u_time;
    // Stokes-style crest sharpening: a phase-locked 2nd harmonic sharpens crests
    // and flattens troughs along each wave's direction.
    float e = sin(phase) - 0.5 * u_steepness * cos(2.0 * phase);
    y += u_amplitude[i] * e;
  }
  return y;
}

void main() {
  vec2 uv = vec2(gl_FragCoord.x / u_resolution.x, 1.0 - gl_FragCoord.y / u_resolution.y);
  float aspect = u_resolution.y / u_resolution.x;
  vec2 world = (uv - 0.5) * vec2(u_extent, u_extent * aspect);

  float h = heightAt(world);

  // Start from the (usually transparent) background and composite lines on top.
  vec3 rgb = u_background.rgb;
  float alpha = u_background.a;

  if (u_contourEnabled) {
    float f = h / u_contourInterval;
    float nearest = floor(f + 0.5);               // index of the closest contour line
    float d = abs(f - nearest);                   // 0 exactly on that line
    float aa = max(fwidth(f), 1e-4);

    // Is the nearest line an index (bold) line? mod() stays positive for negative
    // indices, so troughs are handled the same as crests.
    bool isIndex = u_emphasisEvery > 0 && mod(nearest, float(u_emphasisEvery)) < 0.5;
    float width = isIndex ? u_emphasisWidth : u_contourWidth;
    vec4 lineColor = isIndex ? u_emphasisColor : u_contourColor;

    float line = 1.0 - smoothstep(0.0, aa * max(width, 0.25), d);
    float cov = line * lineColor.a;
    rgb = mix(rgb, lineColor.rgb, cov);
    alpha = alpha + cov * (1.0 - alpha);
  }

  fragColor = vec4(rgb, alpha);
}
`;
